/**
 * Ai-POS · Impresora térmica Bluetooth (Web Bluetooth API)
 * Compatible con impresoras ESC/POS 58mm (32 columnas) y 80mm (48 columnas).
 *
 * Servicios GATT soportados (auto-detección):
 *  - 000018f0-0000-1000-8000-00805f9b34fb (Genérico chino, Xprinter, Milestone, MTP, POS-58)
 *  - 0000ff00-0000-1000-8000-00805f9b34fb (Alternativo POS-58 clone)
 *  - Nordic UART 6e400001-b5a3-f393-e0a9-e50e24dcca9e (algunas variantes)
 *
 * Uso:
 *   import { pairPrinter, printReceipt, isSupported } from "@/lib/btPrinter";
 *   const dev = await pairPrinter();                 // gesture del usuario
 *   await printReceipt(receiptTextOrLines, options); // envía ESC/POS
 */
import QRCode from "qrcode";
import { saleReceiptLines } from "./saleLines";
import { maskRut } from "./rut";


const STORAGE_KEY = "aipos_bt_printer";
const STORAGE_ID_KEY = "aipos_bt_printer_id";
// Impresora recordada por equipo y por caja: { [terminal_id|global]: {id, name, at} }
const STORAGE_MAP_KEY = "aipos_bt_printers";

// Servicios BLE conocidos para impresoras térmicas
const KNOWN_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
];

// Estado en memoria (una única impresora activa por sesión)
let _device = null;
let _characteristic = null;
let _notifyChar = null;
let _scope = "global";          // caja (terminal_id) a la que pertenece la impresora de este equipo
let _reintentando = false;
const _oyentes = new Set();     // avisos de estado a la interfaz (conectada / desconectada)

export function isSupported() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

/** ¿El navegador recuerda los permisos de la impresora entre sesiones? (Chrome con la
 *  opción de permisos persistentes activada: ahí `getDevices()` existe). */
export function hasPersistentPermissions() {
  return isSupported() && typeof navigator.bluetooth.getDevices === "function";
}

/** La caja abierta en este equipo: cada una recuerda su propia impresora. */
export function setPrinterScope(terminalId) {
  const nuevo = terminalId || "global";
  if (nuevo !== _scope) {
    _scope = nuevo;
    _device = null;
    _characteristic = null;
  }
  return _scope;
}

function _mapa() {
  try { return JSON.parse(localStorage.getItem(STORAGE_MAP_KEY) || "{}") || {}; } catch { return {}; }
}

function _guardarEnMapa(id, name) {
  try {
    const m = _mapa();
    m[_scope] = { id: id || null, name: name || null, at: new Date().toISOString() };
    localStorage.setItem(STORAGE_MAP_KEY, JSON.stringify(m));
  } catch { /* ignore */ }
}

/** Impresora recordada para esta caja en este equipo (o la última usada). */
export function savedPrinter() {
  const m = _mapa();
  const d = m[_scope] || m.global || null;
  if (d && (d.id || d.name)) return d;
  const name = getSavedName();
  const id = savedId();
  return name || id ? { id, name } : null;
}

/** Suscripción a los cambios de estado de la impresora (conectada / caída). */
export function onPrinterState(cb) {
  _oyentes.add(cb);
  return () => _oyentes.delete(cb);
}

function _avisar(estado) {
  for (const cb of _oyentes) { try { cb(estado); } catch { /* ignore */ } }
}

export function getSavedName() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}

function saveName(name) {
  try { localStorage.setItem(STORAGE_KEY, name || ""); } catch { /* ignore */ }
}

function saveId(id) {
  try { localStorage.setItem(STORAGE_ID_KEY, id || ""); } catch { /* ignore */ }
}

function savedId() {
  try { return localStorage.getItem(STORAGE_ID_KEY) || null; } catch { return null; }
}

/** ¿Hay una impresora conectada y lista en esta pestaña? */
export function isConnected() {
  return !!(_device && _device.gatt && _device.gatt.connected);
}

/** Recupera el dispositivo ya autorizado sin volver a mostrar el selector.
 *  Sólo funciona si el navegador expone `getDevices()` (Chrome con el backend de permisos
 *  persistentes; nuestro acceso directo de Windows lo activa). */
async function restoreDevice() {
  if (_device) return _device;
  if (!hasPersistentPermissions()) return null;
  try {
    const devs = await navigator.bluetooth.getDevices();
    if (!devs?.length) return null;
    const g = savedPrinter() || {};
    const found =
      (g.id && devs.find((d) => d.id === g.id)) ||
      (g.name && devs.find((d) => d.name === g.name)) ||
      (savedId() && devs.find((d) => d.id === savedId())) ||
      (devs.length === 1 ? devs[0] : null);
    if (found) {
      _device = found;
      _characteristic = null;
      _vigilar(found);
      return found;
    }
  } catch { /* sin permisos persistentes */ }
  return null;
}

/** Si la impresora se apaga o se aleja, se reconecta sola: el cajero no toca nada. */
function _vigilar(dev) {
  if (!dev || dev.__aiposVigilada) return;
  dev.__aiposVigilada = true;
  dev.addEventListener("gattserverdisconnected", async () => {
    _characteristic = null;
    _avisar({ conectada: false, nombre: dev.name || null });
    if (_reintentando) return;
    _reintentando = true;
    for (const espera of [1500, 4000, 10000]) {
      await new Promise((r) => setTimeout(r, espera));
      if (_device !== dev) break;
      try {
        await _ensureConnected();
        _avisar({ conectada: true, nombre: dev.name || null });
        break;
      } catch { /* sigue apagada: se reintenta */ }
    }
    _reintentando = false;
  });
}

/** Intenta conectar con la impresora ya autorizada, sin pedir nada al usuario.
 *  Devuelve true si quedó lista para imprimir. */
export async function tryReconnect() {
  try {
    const dev = await restoreDevice();
    if (!dev) return false;
    await _ensureConnected();
    _avisar({ conectada: true, nombre: dev.name || null });
    return true;
  } catch {
    return false;
  }
}

export function forgetPrinter() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_ID_KEY);
    const m = _mapa();
    delete m[_scope];
    localStorage.setItem(STORAGE_MAP_KEY, JSON.stringify(m));
  } catch { /* ignore */ }
  try { _device?.gatt?.disconnect?.(); } catch { /* ignore */ }
  _device = null;
  _characteristic = null;
  _avisar({ conectada: false, nombre: null });
}

/** Resumen para la interfaz: qué impresora quedó guardada y si se puede recuperar sola. */
export async function btDiagnostico() {
  const guardada = savedPrinter();
  const out = {
    soportado: isSupported(),
    permisosPersistentes: hasPersistentPermissions(),
    guardada,
    conectada: isConnected(),
    recuperable: false,
    autorizadas: 0,
  };
  if (out.permisosPersistentes) {
    try {
      const devs = await navigator.bluetooth.getDevices();
      out.autorizadas = devs?.length || 0;
      out.recuperable = !!(devs || []).find((d) => (guardada?.id && d.id === guardada.id)
        || (guardada?.name && d.name === guardada.name)) || (devs || []).length === 1;
    } catch { /* ignore */ }
  }
  return out;
}

/**
 * Solicita al usuario emparejar una impresora. Requiere gesto (click).
 * Devuelve `{ name, id }` del dispositivo.
 */
export async function pairPrinter() {
  if (!isSupported()) throw new Error("Web Bluetooth no está disponible en este navegador. Usa Chrome/Edge en escritorio o Android.");
  const device = await navigator.bluetooth.requestDevice({
    // Filtro laxo: acepta todo (algunas impresoras no anuncian el servicio hasta conectarse)
    acceptAllDevices: true,
    optionalServices: KNOWN_SERVICES,
  });
  _device = device;
  _characteristic = null; // forzar reconexión
  saveName(device.name || "Impresora BT");
  saveId(device.id);
  _guardarEnMapa(device.id, device.name || "Impresora BT");
  _vigilar(device);
  // Deja la conexión GATT abierta: así los vales siguientes salen sin pedir nada.
  try { await _ensureConnected(); _avisar({ conectada: true, nombre: device.name || null }); } catch { /* se reintenta al imprimir */ }
  return { name: device.name || "Impresora BT", id: device.id };
}

async function _findWritableCharacteristic(server) {
  // Iterar servicios conocidos; si alguno responde, buscar la primera characteristic escribible
  for (const svcUuid of KNOWN_SERVICES) {
    try {
      const service = await server.getPrimaryService(svcUuid);
      const chars = await service.getCharacteristics();
      for (const c of chars) {
        if (c.properties && (c.properties.write || c.properties.writeWithoutResponse)) {
          return c;
        }
      }
    } catch { /* servicio no presente en esta impresora */ }
  }
  // Fallback: recorrer TODOS los servicios primarios y buscar writable
  const services = await server.getPrimaryServices();
  for (const s of services) {
    try {
      const chars = await s.getCharacteristics();
      for (const c of chars) {
        if (c.properties && (c.properties.write || c.properties.writeWithoutResponse)) {
          return c;
        }
      }
    } catch { /* ignore */ }
  }
  throw new Error("La impresora no expone una característica de escritura ESC/POS.");
}

async function _ensureConnected() {
  if (!_device) await restoreDevice();
  if (!_device) {
    const err = new Error(
      "La impresora Bluetooth está configurada, pero este equipo todavía no la conectó. " +
      "Toca «Conectar impresora» una vez y elígela en la lista."
    );
    err.code = "BT_NEEDS_PAIRING";
    throw err;
  }
  if (_device.gatt.connected && _characteristic) return _characteristic;
  let server;
  try {
    server = await _device.gatt.connect();
  } catch (e) {
    // Dispositivo autorizado pero fuera de alcance / apagado / no anunciando
    if (typeof _device.watchAdvertisements === "function") {
      try {
        await _device.watchAdvertisements();
        await new Promise((res, rej) => {
          const t = setTimeout(() => rej(new Error("timeout")), 6000);
          _device.addEventListener("advertisementreceived", () => { clearTimeout(t); res(); }, { once: true });
        });
        server = await _device.gatt.connect();
      } catch {
        const err = new Error("No se pudo conectar con la impresora. Revisa que esté encendida y cerca.");
        err.code = "BT_UNREACHABLE";
        throw err;
      }
    } else {
      const err = new Error("No se pudo conectar con la impresora. Revisa que esté encendida y cerca.");
      err.code = "BT_UNREACHABLE";
      throw err;
    }
  }
  const ch = await _findWritableCharacteristic(server);
  _characteristic = ch;
  // Canal de respuesta: no todas las impresoras lo tienen. Si existe, podemos consultar
  // si se quedó sin papel o con la tapa abierta (ESC/POS DLE EOT).
  try {
    _notifyChar = await _findNotifyCharacteristic(server);
    if (_notifyChar) await _notifyChar.startNotifications();
  } catch { _notifyChar = null; }
  _device.addEventListener("gattserverdisconnected", () => { _characteristic = null; _notifyChar = null; });
  return ch;
}

async function _findNotifyCharacteristic(server) {
  const services = await server.getPrimaryServices();
  for (const s of services) {
    try {
      for (const c of await s.getCharacteristics()) {
        if (c.properties?.notify) return c;
      }
    } catch { /* ignore */ }
  }
  return null;
}

/** Estado real de la impresora térmica (ESC/POS DLE EOT).
 *  Devuelve null si la impresora no tiene canal de respuesta (no se puede saber). */
export async function queryPrinterStatus() {
  if (!_device || !_device.gatt?.connected) return { offline: true, paper_out: false, cover_open: false };
  if (!_notifyChar) return null;
  const ask = (n) => new Promise((resolve) => {
    const ch = _notifyChar;
    let done = false;
    const onVal = (e) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      ch?.removeEventListener("characteristicvaluechanged", onVal);
      resolve(e.target.value?.byteLength ? e.target.value.getUint8(0) : null);
    };
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      ch?.removeEventListener("characteristicvaluechanged", onVal);
      resolve(null);
    }, 1200);
    if (!ch) { done = true; clearTimeout(timer); resolve(null); return; }
    ch.addEventListener("characteristicvaluechanged", onVal);
    _writeBytes(new Uint8Array([0x10, 0x04, n])).catch(() => {});
  });
  try {
    const s1 = await ask(1);
    const s2 = await ask(2);
    const s4 = await ask(4);
    if (s1 === null && s2 === null && s4 === null) return null;
    return {
      offline: s1 !== null && (s1 & 0x08) !== 0,
      cover_open: s2 !== null && (s2 & 0x04) !== 0,
      paper_out: (s2 !== null && (s2 & 0x20) !== 0) || (s4 !== null && (s4 & 0x60) !== 0),
      paper_low: s4 !== null && (s4 & 0x0c) !== 0,
    };
  } catch {
    return null;
  }
}

const _espera = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Envía a la impresora con control de flujo: trozos chicos y pausas para no desbordar el
 * buffer (4-8 KB) de las térmicas de 58 mm, que se bloquean si reciben más rápido de lo que imprimen.
 * Con escritura confirmada (write) Bluetooth espera cada acuse; sin ella se dosifica por tiempo.
 */
async function _writeBytes(bytes) {
  const ch = await _ensureConnected();
  const conAcuse = !!ch.properties.write;
  const chunkSize = 100;
  let desdePausa = 0;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const slice = bytes.slice(i, i + chunkSize);
    if (conAcuse) await ch.writeValue(slice);
    else { await ch.writeValueWithoutResponse(slice); await _espera(12); }
    desdePausa += slice.length;
    // Cada ~2 KB una pausa para que el cabezal alcance a vaciar el buffer (≈ 15-20 KB/s efectivos)
    if (desdePausa >= 2048) { await _espera(conAcuse ? 40 : 90); desdePausa = 0; }
  }
}

// ==================== ESC/POS helpers ====================

const ESC = 0x1b;
const GS = 0x1d;

// Concatena Uint8Arrays
const cat = (...arrs) => {
  const total = arrs.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) { out.set(a, off); off += a.length; }
  return out;
};

const bytes = (...bs) => new Uint8Array(bs);

// Convierte string a CP437/Latin1 aproximado. Reemplaza acentos y caracteres
// no ASCII para máxima compatibilidad con impresoras chinas 58mm.
// Mensaje de cierre configurable por terminal (varias líneas). Vacío = mensaje por defecto.
function graciasLines(msg) {
  const lines = String(msg ?? "").split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.length ? lines : ["Gracias por tu compra!"];
}

function encodeText(str) {
  const normalized = String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quita acentos
    .replace(/ñ/g, "n").replace(/Ñ/g, "N")
    .replace(/¿/g, "?").replace(/¡/g, "!")
    .replace(/[^\x00-\x7F]/g, "?");   // fallback para el resto
  const out = new Uint8Array(normalized.length);
  for (let i = 0; i < normalized.length; i++) out[i] = normalized.charCodeAt(i) & 0xff;
  return out;
}

// Comandos base
const INIT = bytes(ESC, 0x40);                       // ESC @ · reset
const LF = bytes(0x0a);
const ALIGN_LEFT = bytes(ESC, 0x61, 0x00);
const ALIGN_CENTER = bytes(ESC, 0x61, 0x01);
const BOLD_ON = bytes(ESC, 0x45, 0x01);
const BOLD_OFF = bytes(ESC, 0x45, 0x00);
const DOUBLE_ON = bytes(GS, 0x21, 0x11);             // doble alto + doble ancho
const TALL_ON = bytes(GS, 0x21, 0x01);               // doble alto (mantiene las columnas)
const TALL_OFF = bytes(GS, 0x21, 0x00);
const DOUBLE_OFF = bytes(GS, 0x21, 0x00);
const CUT = bytes(GS, 0x56, 0x00);                   // corte total
const FEED_N = (n) => bytes(ESC, 0x64, n & 0xff);    // avanza n líneas

// ============ QR Code ESC/POS commands (Model 2) ============
// Comandos GS ( k para impresoras compatibles (mayoría de 58mm/80mm modernas)
// Referencia: EPSON ESC/POS Command Reference.
function qrModel() {                     // Selecciona modelo (2)
  return bytes(GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00);
}
function qrSize(size = 6) {              // Tamaño módulo (1-16). 6 ≈ 24mm en 58mm
  return bytes(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, size & 0xff);
}
function qrErrorCorrection(level = 0x31) { // L=48, M=49, Q=50, H=51
  return bytes(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, level & 0xff);
}
function qrStore(data) {                  // Guarda datos en buffer
  const enc = encodeText(data);
  const total = enc.length + 3;
  const pL = total & 0xff;
  const pH = (total >> 8) & 0xff;
  const header = bytes(GS, 0x28, 0x6B, pL, pH, 0x31, 0x50, 0x30);
  return cat(header, enc);
}
function qrPrint() {                      // Imprime QR del buffer
  return bytes(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30);
}
function qrCode(data, size = 6) {
  return cat(qrModel(), qrSize(size), qrErrorCorrection(0x31), qrStore(data), qrPrint());
}

// QR como imagen raster (GS v 0): compatible con CUALQUIER impresora 80mm, incluso
// las que no soportan el comando nativo GS ( k. `scale` = dots por módulo.
function qrRaster(data, scale = 5, margin = 3) {
  let qr;
  try { qr = QRCode.create(String(data), { errorCorrectionLevel: "M" }); }
  catch { return new Uint8Array(0); }
  const n = qr.modules.size;
  const md = qr.modules.data; // 1 = módulo oscuro
  const dim = (n + margin * 2) * scale;
  const rowBytes = Math.ceil(dim / 8);
  const raster = new Uint8Array(rowBytes * dim);
  for (let y = 0; y < dim; y++) {
    const my = Math.floor(y / scale) - margin;
    for (let x = 0; x < dim; x++) {
      const mx = Math.floor(x / scale) - margin;
      if (my >= 0 && my < n && mx >= 0 && mx < n && md[my * n + mx]) {
        raster[y * rowBytes + (x >> 3)] |= (0x80 >> (x & 7));
      }
    }
  }
  const header = bytes(GS, 0x76, 0x30, 0x00, rowBytes & 0xff, (rowBytes >> 8) & 0xff, dim & 0xff, (dim >> 8) & 0xff);
  return cat(header, raster);
}

// ============ Barcode ESC/POS commands (GS k format) ============
// GS k m d1..dn NUL     (formato clásico, m=0..6)  o
// GS k m n d1..dn        (formato moderno, m=65..79, n=length)
// Usamos CODE39 (m=69, 0x45) por compatibilidad amplia con impresoras chinas.
function barcodeHeight(dots = 80) {         // GS h n · altura en dots (0-255)
  return bytes(GS, 0x68, dots & 0xff);
}
function barcodeWidth(w = 2) {               // GS w n · ancho módulo (2-6)
  return bytes(GS, 0x77, Math.max(2, Math.min(6, w)) & 0xff);
}
function barcodeHRI(pos = 2) {               // GS H n · posición texto HRI (0 none, 2 bajo)
  return bytes(GS, 0x48, pos & 0xff);
}
function barcode(data, { height = 80, width = 2, type = "CODE39" } = {}) {
  // CODE39 acepta 0-9, A-Z, espacio, y $ % + - . /  → convertimos a mayúscula
  const clean = String(data).toUpperCase().replace(/[^0-9A-Z\-\.\ \$\/\+\%]/g, "");
  const enc = encodeText(clean);
  const m = type === "CODE128" ? 73 : 69;  // 73=CODE128, 69=CODE39
  const header = bytes(GS, 0x6B, m, enc.length);
  return cat(barcodeHeight(height), barcodeWidth(width), barcodeHRI(2), header, enc);
}

// Layout: pad para 2 columnas (nombre + monto) según ancho
function twoCol(left, right, width) {
  const l = String(left);
  const r = String(right);
  if (l.length + r.length + 1 >= width) {
    // línea 1: left truncado; línea 2: right alineado a la derecha
    const line1 = l.slice(0, width);
    const pad = Math.max(1, width - r.length);
    const line2 = " ".repeat(pad) + r;
    return line1 + "\n" + line2;
  }
  return l + " ".repeat(width - l.length - r.length) + r;
}

/**
 * Construye los bytes ESC/POS para un ticket estructurado.
 * receipt = {
 *   header: string[],           // líneas centradas (nombre local, dirección)
 *   subheader?: string[],       // líneas centradas más pequeñas (venta #, fecha)
 *   items: [{qty, name, price}],
 *   totals: [{label, value, bold?}],
 *   footer?: string[],
 *   width?: 32 | 48,            // 32=58mm, 48=80mm (default 32)
 *   cut?: boolean,              // corte al final (default true)
 * }
 */
const _fmtFecha = (v) => { const x = String(v || ""); return x.length === 8 ? `${x.slice(0, 2)}/${x.slice(2, 4)}/${x.slice(4)}` : x; };
const _fmtHora = (v) => { const x = String(v || ""); return x.length === 6 ? `${x.slice(0, 2)}:${x.slice(2, 4)}:${x.slice(4)}` : x; };
const _sinDato = (v) => !v || /^0+$/.test(String(v));
export const tbkCuenta = (v) => (_sinDato(v) || /^30+$/.test(String(v)) ? "" : `*******${String(v).replace(/\D/g, "").slice(-3)}`);
export const tbkFechaContable = (v) => {
  const x = String(v || "");
  if (_sinDato(x)) return "";
  return x.length === 4 ? `${x.slice(0, 2)}-${x.slice(2)}` : x.length === 6 ? `${x.slice(0, 2)}-${x.slice(2, 4)}-${x.slice(4)}` : x;
};

/** Líneas del comprobante Transbank para ESC/POS: [{t, c (centrado), b (negrita), big}]. */
export function tbkVoucherLines(tbk, width = 48) {
  const clp = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
  const c = tbk.comercio_datos || {};
  const credito = String(tbk.tipo_tarjeta || "").toUpperCase().startsWith("CR");
  const up = (v) => String(v || "").toUpperCase();
  const L = [{ t: "COMPROBANTE DE VENTA", c: 1, b: 1 }, { t: `TARJETA DE ${credito ? "CREDITO" : "DEBITO"}`, c: 1, big: 1 }];
  for (const v of [c.nombre_fantasia, c.razon_social, c.direccion, c.rut, c.ciudad]) if (v) L.push({ t: up(v), c: 1 });
  L.push({ t: [tbk.comercio, tbk.terminal].filter(Boolean).join("-"), c: 1 }, { t: "" });
  const tres = (a, b, d) => { const pad = Math.max(1, width - a.length - b.length - d.length); const l = Math.floor(pad / 2); return a + " ".repeat(l) + b + " ".repeat(pad - l) + d; };
  L.push({ t: tres("FECHA", "HORA", "TERMINAL"), b: 1 });
  L.push({ t: tres(_fmtFecha(tbk.fecha), _fmtHora(tbk.hora), String(tbk.terminal || "")) });
  const fc = tbkFechaContable(tbk.fecha_contable);
  if (fc) L.push({ t: twoCol("FECHA CONTABLE", fc, width) });
  L.push({ t: twoCol("NUMERO DE TARJETA", `************${tbk.ultimos4 || ""}`, width) });
  const cta = credito ? "" : tbkCuenta(tbk.num_cuenta);
  if (cta) L.push({ t: twoCol("NUMERO DE CUENTA", cta, width) });
  L.push({ t: `${up(tbk.marca)} ${credito ? "CREDITO" : "DEBITO"}`.trim(), b: 1 });
  L.push({ t: twoCol("MONTO COMPRA", clp(tbk.monto), width) });
  if (credito && Number(tbk.cuotas) > 1) {
    L.push({ t: twoCol("N CUOTAS", String(tbk.cuotas), width) });
    if (tbk.monto_cuota) L.push({ t: twoCol("MONTO CUOTA", clp(tbk.monto_cuota), width) });
  }
  L.push({ t: twoCol("TOTAL:", clp(tbk.monto), width), big: 1 });
  L.push({ t: twoCol("NUMERO DE OPERACION", String(tbk.operacion || ""), width) });
  L.push({ t: twoCol("CODIGO DE AUTORIZACION", String(tbk.autorizacion || ""), width) });
  L.push({ t: "" }, { t: "GRACIAS POR SU COMPRA", c: 1 }, { t: "COPIA CLIENTE", c: 1 }, { t: "ACEPTO PAGAR SEGUN CONTRATO CON EMISOR", c: 1 });
  return L;
}

export function buildTicketBytes(receipt) {
  const width = receipt.width || 32;
  const chunks = [INIT, ALIGN_CENTER];

  // Header - primera línea en doble tamaño si es corta
  const header = receipt.header || [];
  header.forEach((line, idx) => {
    if (idx === 0 && line.length <= width / 2) {
      chunks.push(DOUBLE_ON, encodeText(line), LF, DOUBLE_OFF);
    } else {
      chunks.push(BOLD_ON, encodeText(line), LF, BOLD_OFF);
    }
  });

  // Cada línea puede ser texto simple o { text, bold } (p.ej. "VENTA #OFF-…" de una venta offline)
  (receipt.subheader || []).forEach((line) => {
    const text = typeof line === "string" ? line : line?.text || "";
    const bold = typeof line === "object" && line?.bold;
    if (bold) chunks.push(BOLD_ON);
    chunks.push(encodeText(text), LF);
    if (bold) chunks.push(BOLD_OFF);
  });

  if (receipt.atencion) {
    chunks.push(LF, BOLD_ON, encodeText("NUMERO DE ATENCION"), LF, BOLD_OFF,
      bytes(GS, 0x21, 0x33), encodeText(String(receipt.atencion)), LF, DOUBLE_OFF);
  }

  chunks.push(LF, encodeText("-".repeat(width)), LF, ALIGN_LEFT);

  (receipt.items || []).forEach((it) => {
    const qtyName = `${it.qty}x ${it.name}`;
    chunks.push(encodeText(twoCol(qtyName, it.price, width)), LF);
  });

  chunks.push(ALIGN_LEFT, LF, encodeText("-".repeat(width)), LF);

  (receipt.totals || []).forEach((t) => {
    // `size: "big"` → doble alto + negrita (TOTAL y VUELTO)
    if (t.size === "big") chunks.push(TALL_ON, BOLD_ON);
    else if (t.bold) chunks.push(BOLD_ON);
    chunks.push(encodeText(twoCol(t.label, t.value, width)), LF);
    if (t.size === "big") chunks.push(BOLD_OFF, TALL_OFF);
    else if (t.bold) chunks.push(BOLD_OFF);
  });

  // Código de barras (opcional) — ideal para vales de pre-venta y suspendidas.
  if (receipt.barcode) {
    chunks.push(LF, ALIGN_CENTER, barcode(receipt.barcode, { height: 60, width: 2 }), LF);
    chunks.push(ALIGN_LEFT);
  }

  // Código QR como imagen raster (universal: funciona en impresoras sin soporte GS ( k).
  if (receipt.qrCode) {
    chunks.push(LF, ALIGN_CENTER, qrRaster(String(receipt.qrCode), receipt.qrScale || 5), LF);
    chunks.push(ALIGN_LEFT);
  }

  if (receipt.footer && receipt.footer.length) {
    chunks.push(LF, ALIGN_CENTER);
    receipt.footer.forEach((line) => chunks.push(encodeText(line), LF));
  }

  // Bloque alineado a la izquierda (campos para escribir a mano: Nombre, Contacto...)
  if (receipt.footerLeft && receipt.footerLeft.length) {
    chunks.push(ALIGN_LEFT);
    receipt.footerLeft.forEach((line) => chunks.push(encodeText(line), LF));
  }

  if (receipt.footerEnd && receipt.footerEnd.length) {
    chunks.push(ALIGN_CENTER);
    receipt.footerEnd.forEach((line) => chunks.push(encodeText(line), LF));
    chunks.push(ALIGN_LEFT);
  }

  // Comprobante Transbank (copia cliente) al final de la boleta
  if (receipt.tbk?.autorizacion) {
    chunks.push(LF, ALIGN_LEFT, encodeText("-".repeat(width)), LF);
    for (const l of tbkVoucherLines(receipt.tbk, width)) {
      chunks.push(l.c ? ALIGN_CENTER : ALIGN_LEFT);
      if (l.big) chunks.push(TALL_ON, BOLD_ON); else if (l.b) chunks.push(BOLD_ON);
      chunks.push(encodeText(l.t), LF);
      if (l.big) chunks.push(BOLD_OFF, TALL_OFF); else if (l.b) chunks.push(BOLD_OFF);
    }
    chunks.push(ALIGN_LEFT);
  }

  // Avance de líneas antes del corte (configurable por terminal para que el corte
  // no quede sobre la última línea impresa). Default 4.
  chunks.push(FEED_N(receipt.feedBeforeCut != null ? receipt.feedBeforeCut : 4));
  if (receipt.cut !== false) chunks.push(CUT);

  return cat(...chunks);
}

/**
 * Imprime un ticket estructurado.
 */
export async function printReceipt(receipt) {
  if (!isSupported()) throw new Error("Web Bluetooth no está disponible en este navegador.");
  const bs = buildTicketBytes(receipt);
  await _writeBytes(bs);
}

/**
 * Envía a la impresora el comando ESC/POS de apertura de gaveta.
 * El backend es quien autoriza y entrega los bytes (base64); acá sólo se transportan.
 */
export async function sendRawBase64(b64) {
  if (!isSupported()) throw new Error("Web Bluetooth no está disponible en este navegador.");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  await _writeBytes(bytes);
}

/**
 * Imprime una imagen (data URL / URL) como raster ESC/POS (GS v 0).
 * Se usa para la boleta tradicional con timbre PDF417: se renderiza el comprobante
 * a PNG y se envía como imagen, así sale igual que en la impresora de 80 mm.
 * `dots` = ancho del cabezal (384 en 58 mm, 576 en 80 mm).
 */
export async function printImageDataUrl(dataUrl, dots = 384, opts = {}) {
  if (!isSupported()) throw new Error("Web Bluetooth no está disponible en este navegador.");
  await _writeBytes(await rasterBytesFromDataUrl(dataUrl, dots, opts));
}

/** Imagen → bytes ESC/POS raster (GS v 0) por bandas, con avance y corte al final. */
export async function rasterBytesFromDataUrl(dataUrl, dots = 384, opts = {}) {
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  // natural: no agrandar imágenes chicas (timbre PDF417: escalarlo lo vuelve ilegible)
  const base = opts.natural ? Math.min(dots, img.width) : dots;
  const width = Math.max(8, Math.floor(base / 8) * 8);
  const height = Math.max(1, Math.round((img.height / img.width) * width));
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const cx = canvas.getContext("2d");
  cx.fillStyle = "#fff"; cx.fillRect(0, 0, width, height);
  cx.drawImage(img, 0, 0, width, height);
  const data = cx.getImageData(0, 0, width, height).data;
  const rowBytes = width / 8;
  const umbral = opts.threshold ?? 160;
  // Se envía por bandas: muchas impresoras no aceptan un raster muy alto de una vez.
  const banda = 128;
  const chunks = opts.bare ? [ALIGN_CENTER] : [INIT, ALIGN_CENTER];
  for (let y0 = 0; y0 < height; y0 += banda) {
    const h = Math.min(banda, height - y0);
    const raster = new Uint8Array(rowBytes * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < width; x++) {
        const i = ((y0 + y) * width + x) * 4;
        const lum = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
        if (lum < umbral) raster[y * rowBytes + (x >> 3)] |= (0x80 >> (x & 7));
      }
    }
    chunks.push(bytes(GS, 0x76, 0x30, 0x00, rowBytes & 0xff, (rowBytes >> 8) & 0xff, h & 0xff, (h >> 8) & 0xff));
    chunks.push(raster);
  }
  if (opts.bare) chunks.push(LF, ALIGN_LEFT);
  else chunks.push(LF, FEED_N(opts.feedBeforeCut ?? 3), CUT);
  return cat(...chunks);
}

const _clp = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
const _fecha = (iso) => { const s = String(iso || "").slice(0, 10).split("-"); return s.length === 3 ? `${s[2]}-${s[1]}-${s[0]}` : String(iso || ""); };

/**
 * Boleta/factura "liviana" para Bluetooth 58 mm: todo el texto con la letra de la impresora
 * (pocos bytes) y solo el timbre PDF417 como imagen. ~5 veces menos datos que la boleta en imagen.
 * Mantiene lo exigido por el SII: emisor, RUT, giro, dirección, tipo y folio, fecha, detalle, totales y timbre.
 */
export async function buildBoletaLivianaBytes(legal, ctx = {}, width = 32) {
  const { sale, company, dte } = legal || {};
  const e = dte.emisor || {}, r = dte.receptor || {}, t = dte.totales || {};
  const imp = dte.impresion_emisor || {};
  const cm = imp.casa_matriz || { direccion: e.direccion || company?.direccion, comuna: e.comuna || company?.comuna };
  const lugar = (d) => [d?.direccion, d?.comuna].filter(Boolean).join(", ");
  const sep = encodeText("-".repeat(width));
  const out = [INIT, ALIGN_CENTER, BOLD_ON, encodeText(e.razon_social || company?.nombre_comercial || ""), LF, BOLD_OFF,
    encodeText(`RUT ${e.rut || company?.rut || ""}`), LF];
  if (e.giro || company?.giro) out.push(encodeText(String(e.giro || company?.giro).slice(0, width * 2)), LF);
  if (lugar(cm)) out.push(encodeText(lugar(cm)), LF);
  if (imp.sucursal) out.push(encodeText(`Suc: ${lugar(imp.sucursal)}`), LF);
  out.push(sep, LF, BOLD_ON, encodeText(String(dte.tipo_nombre || "DOCUMENTO ELECTRONICO").toUpperCase()), LF,
    DOUBLE_ON, encodeText(`N ${dte.folio}`), LF, DOUBLE_OFF, BOLD_OFF);
  const hora = sale.created_at ? new Date(sale.created_at).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/Santiago" }) : "";
  out.push(encodeText(`${_fecha(dte.fecha_emision)} ${hora}`), LF);
  if (ctx.caja || ctx.cashier) out.push(encodeText([ctx.caja, ctx.cashier].filter(Boolean).join(" · ")), LF);
  const esCarta = ["33", "34", "46", "52", "56", "61"].includes(String(dte.tipo_dte));
  out.push(ALIGN_LEFT);
  if (esCarta) {
    out.push(sep, LF, encodeText(`Cliente: ${r.razon_social || sale.customer_name || ""}`), LF, encodeText(`RUT: ${r.rut || sale.customer_rut || ""}`), LF);
    if (r.giro || sale.customer_giro) out.push(encodeText(`Giro: ${r.giro || sale.customer_giro}`), LF);
    if (r.direccion || sale.customer_address) out.push(encodeText(`Dir: ${r.direccion || sale.customer_address}`), LF);
  } else if (r.rut && r.rut !== "66666666-6") out.push(encodeText(`Cliente RUT ${maskRut(r.rut)}`), LF);
  out.push(sep, LF);
  const { items, subtotal_normal, discounts, extras } = saleReceiptLines(sale);
  for (const it of items) out.push(encodeText(twoCol(`${it.cantidad} ${it.nombre}${it.es_exento ? " [E]" : ""}`, _clp(it.amount_normal), width)), LF);
  out.push(sep, LF);
  const fila = (a, b) => out.push(encodeText(twoCol(a, b, width)), LF);
  if (sale.numero_atencion) out.push(ALIGN_CENTER, encodeText("N DE ATENCION"), LF, DOUBLE_ON, encodeText(String(sale.numero_atencion)), LF, DOUBLE_OFF, ALIGN_LEFT);
  if (discounts.length) { fila("Subtotal", _clp(subtotal_normal)); for (const d of discounts) fila(d.label, `-${_clp(d.value)}`); }
  for (const dd of dte.descuentos || []) fila(dd.glosa || "Descuento", `${dd.tipo === "D" ? "-" : "+"}${_clp(dd.valor)}`);
  for (const ex of extras) fila(ex.label, _clp(ex.value));
  if (t.exento > 0) fila("Exento", _clp(t.exento));
  if (t.neto > 0) fila("Neto", _clp(t.neto));
  if (t.iva > 0) fila("IVA 19%", _clp(t.iva));
  out.push(TALL_ON, BOLD_ON); fila("TOTAL", _clp(t.total)); out.push(BOLD_OFF, TALL_OFF);
  if (sale.redondeo_ajuste) { fila("Redondeo", _clp(sale.redondeo_ajuste)); fila("A PAGAR", _clp(sale.total_pagar ?? sale.total)); }
  if (ctx.paymentLines?.length) for (const pl of ctx.paymentLines) fila(pl.label, _clp(pl.value));
  else fila("Pago", ctx.paymentLabel || sale.payment_label || String(sale.payment_method || "").replace(/_/g, " "));
  if (sale.vuelto) { out.push(BOLD_ON); fila("VUELTO", _clp(sale.vuelto)); out.push(BOLD_OFF); }
  const obs = dte.observaciones || sale.doc_observaciones;
  if (obs) out.push(sep, LF, encodeText(`Obs: ${obs}`), LF);
  const tr = dte.transporte;
  const refs = dte.referencias_doc || [];
  if (refs.length) {
    out.push(sep, LF, BOLD_ON, encodeText("REFERENCIAS"), LF, BOLD_OFF);
    for (const r of refs) {
      out.push(encodeText(`${r.nombre}`.slice(0, width * 2)), LF, encodeText(`  N ${r.folio} del ${_fecha(r.fecha)}`), LF);
      if (r.razon && r.razon !== r.nombre) out.push(encodeText(`  ${r.razon}`.slice(0, width * 2)), LF);
    }
  }
  if (tr && (tr.patente || tr.rut_chofer || tr.rut_transportista)) {
    out.push(sep, LF, BOLD_ON, encodeText("TRANSPORTE"), LF, BOLD_OFF);
    if (tr.nombre_chofer) out.push(encodeText(`Chofer: ${tr.nombre_chofer}`), LF, encodeText(`RUT chofer: ${tr.rut_chofer || "-"}`), LF);
    if (tr.rut_transportista) out.push(encodeText(`RUT transp.: ${tr.rut_transportista}`), LF);
    if (tr.patente) out.push(encodeText(`Patente: ${tr.patente}`), LF);
  }
  if (ctx.cedible) {
    out.push(LF, ALIGN_CENTER, BOLD_ON, encodeText("ACUSE DE RECIBO"), LF, BOLD_OFF, ALIGN_LEFT);
    for (const l of ["Nombre", "RUT", "Fecha", "Firma"]) out.push(LF, encodeText(`${l}: ${"_".repeat(Math.max(4, width - l.length - 2))}`), LF);
  }
  if (dte.pdf417) {
    out.push(LF, await rasterBytesFromDataUrl(dte.pdf417, 384, { natural: true, bare: true }));
    out.push(ALIGN_CENTER, encodeText("Timbre Electronico SII"), LF);
    if (dte.resolucion_num) out.push(encodeText(`Res. N ${dte.resolucion_num} de ${_fecha(dte.resolucion_fecha)}`), LF);
    out.push(encodeText("Verifique documento en www.sii.cl"), LF);
    if (dte.ambiente === "CERT") out.push(BOLD_ON, encodeText("AMBIENTE DE CERTIFICACION"), LF, BOLD_OFF);
  }
  out.push(ALIGN_CENTER, LF);
  for (const l of graciasLines(ctx.gracias_message)) out.push(encodeText(l), LF);
  for (const line of String(ctx.footer_message || "").split("\n")) if (line.trim()) out.push(encodeText(line.trim()), LF);
  if (ctx.cedible) out.push(DOUBLE_ON, encodeText("CEDIBLE"), LF, DOUBLE_OFF);
  else if (sale.tbk?.autorizacion) {
    out.push(ALIGN_LEFT, sep, LF);
    for (const l of tbkVoucherLines(sale.tbk, width)) {
      out.push(l.c ? ALIGN_CENTER : ALIGN_LEFT);
      if (l.b || l.big) out.push(BOLD_ON);
      out.push(encodeText(l.t), LF);
      if (l.b || l.big) out.push(BOLD_OFF);
    }
  }
  out.push(ALIGN_LEFT, FEED_N(ctx.feedBeforeCut ?? 4), CUT);
  return cat(...out);
}

export async function printBoletaLiviana(legal, ctx = {}) {
  if (!isSupported()) throw new Error("Web Bluetooth no está disponible en este navegador.");
  await _writeBytes(await buildBoletaLivianaBytes(legal, ctx));
}

/**
 * Adapta una venta de Ai-POS a la estructura del ticket.
 * `sale` es lo que se guarda en `lastSale` (backend response).
 */
export function saleToReceipt(sale, ctx = {}) {
  const clp = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
  const width = ctx.width || 32;
  const header = [
    ctx.company_name || "Ai-POS",
    ctx.store_name || "",
  ].filter(Boolean);
  const presCodes = sale.presale_codes || ctx.presaleCodes || [];
  const esTicket = sale.ticket_sin_dte || sale.emision?.doc_type === "TICKET";
  const subheader = [
    esTicket ? { text: "TICKET - NO VALIDO COMO BOLETA", bold: true } : null,
    // Venta offline: la línea va destacada en negrita para distinguirla del folio real
    sale.offline ? { text: `VENTA #${sale.numero}`, bold: true } : `Venta #${sale.numero}`,
    new Date(sale.created_at || Date.now()).toLocaleString("es-CL"),
    ctx.caja ? `Caja: ${ctx.caja}` : null,
    ctx.cashier ? `Cajero: ${ctx.cashier}` : null,
    presCodes.length ? `Ticket Pre-venta: ${presCodes.join("-")}` : null,
  ].filter(Boolean);
  const { items: normItems, subtotal_normal, discounts, extras } = saleReceiptLines(sale);
  const items = [];
  normItems.forEach((it) => {
    items.push({ qty: it.cantidad, name: it.nombre, price: clp(it.amount_normal) });
  });
  const totals = [];
  // Productos a precio normal arriba · descuentos detallados al final
  if (discounts.length) {
    totals.push({ label: "Subtotal", value: clp(subtotal_normal) });
    discounts.forEach((d) => totals.push({ label: d.label, value: `-${clp(d.value)}` }));
  }
  extras.forEach((ex) => totals.push({ label: ex.label, value: clp(ex.value) }));
  const exento = Math.round(sale.total_exento || 0);
  const afecto = Math.round(sale.total_afecto ?? (sale.total - exento));
  if (exento > 0) totals.push({ label: "Exento", value: clp(exento) });
  if (afecto > 0) {
    const ivaAfecto = Math.round(sale.iva_afecto ?? sale.iva ?? 0);
    totals.push({ label: "Neto", value: clp(Math.round(sale.neto_afecto ?? (afecto - ivaAfecto))) });
    totals.push({ label: "IVA", value: clp(ivaAfecto) });
  }
  totals.push({ label: "TOTAL", value: clp(sale.total), size: "big" });
  if (sale.redondeo_ajuste) {
    totals.push({ label: "Redondeo", value: clp(sale.redondeo_ajuste) });
    totals.push({ label: "A PAGAR", value: clp(sale.total_pagar ?? sale.total), size: "big" });
  }
  // Pago: en mixto se detalla cada medio con su monto (no la palabra "MIXTO")
  if (ctx.paymentLines && ctx.paymentLines.length) {
    ctx.paymentLines.forEach((pl) => totals.push({ label: pl.label, value: clp(pl.value) }));
  } else {
    totals.push({ label: "Pago", value: ctx.paymentLabel || sale.payment_label || sale.payment_method });
  }
  if (sale.credito) {
    totals.push({ label: (sale.credito.nombre || "").slice(0, 24), value: "" });
    totals.push({ label: "Cupo disponible", value: clp(sale.credito.disponible) });
  }
  if (sale.vuelto) totals.push({ label: "VUELTO", value: clp(sale.vuelto), bold: true });
  // Fidelización: puntos ganados y saldo acumulado tras esta venta
  if (sale.loyalty_resumen) {
    totals.push({ label: "Puntos esta compra", value: clp(sale.loyalty_resumen.gana || 0) });
    totals.push({ label: "Puntos acumulados", value: clp(sale.loyalty_resumen.acumulado || 0), bold: true });
  }

  const footer = [...graciasLines(ctx.gracias_message)];
  if (sale.quote_numero || sale.quote_id) footer.push(`Segun cotizacion ${sale.quote_numero || sale.quote_id}`);
  if (sale.customer_name || sale.customer_rut) {
    footer.push(`Cliente: ${[sale.customer_name, sale.customer_rut].filter(Boolean).join(" - ")}`);
  }
  if (sale.customer_rut) footer.unshift(`RUT: ${maskRut(sale.customer_rut)}`);
  if (sale.customer_name) footer.unshift(`Cliente: ${sale.customer_name}`);
  if (sale.offline) footer.push("Comprobante OFFLINE");
  // Mensaje libre de la sucursal (horarios, redes, etc.)
  for (const line of String(ctx.footer_message || "").split("\n")) {
    if (line.trim()) footer.push(line.trim());
  }

  return { header, subheader, atencion: sale.numero_atencion || null, items, totals, footer, width, qrCode: ctx.qr_data || null, feedBeforeCut: ctx.feedBeforeCut, tbk: sale.tbk || null };
}

/**
 * Comprobante de cierre de caja por medio de pago (Doc 06 §12).
 */
export function cashCloseToReceipt(cuad, ctx = {}) {
  const clp = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
  const width = ctx.width || 32;
  const header = [ctx.company_name || "Ai-POS", ctx.store_name || "", "CIERRE DE CAJA"].filter(Boolean);
  const subheader = [
    `Caja: ${cuad.caja || "-"}`,
    `Apertura: ${new Date(cuad.opened_at).toLocaleString("es-CL")}`,
    `Cierre: ${cuad.closed_at ? new Date(cuad.closed_at).toLocaleString("es-CL") : "-"}`,
    ctx.user_name ? `Cajero: ${ctx.user_name}` : null,
  ].filter(Boolean);
  const items = (cuad.medios || []).map((m) => ({
    qty: m.n, name: m.nombre || m.codigo.replace(/_/g, " "), price: clp(m.monto),
  }));
  const totals = [
    { label: "Ventas cobradas", value: clp(cuad.ventas_cobrado) },
    { label: "Total documentos", value: clp(cuad.ventas_documento) },
  ];
  if (cuad.redondeo_dia) totals.push({ label: "Redondeo del dia", value: clp(cuad.redondeo_dia) });
  if ((cuad.abonos_credito || []).length) {
    totals.push({ label: "PAGOS DE CREDITO", value: "", bold: true });
    cuad.abonos_credito.forEach((m) => totals.push({ label: `${m.nombre} (${m.n})`, value: clp(m.monto) }));
    totals.push({ label: "Total pagos credito", value: clp(cuad.abonos_total), bold: true });
    totals.push({ label: "TOTAL GENERAL", value: "", bold: true });
    (cuad.total_general || []).forEach((m) => totals.push({ label: m.nombre, value: clp(m.monto) }));
    totals.push({ label: "Total recibido", value: clp(cuad.total_general_monto), bold: true });
  }
  totals.push({ label: "Fondo inicial", value: clp(cuad.fondo_inicial) });
  if (cuad.ingresos) totals.push({ label: "Ingresos", value: clp(cuad.ingresos) });
  if (cuad.retiros) totals.push({ label: "Retiros", value: clp(cuad.retiros) });
  totals.push({ label: "Efectivo esperado", value: clp(cuad.efectivo_esperado), bold: true });
  if (cuad.efectivo_contado != null) {
    totals.push({ label: "Efectivo contado", value: clp(cuad.efectivo_contado) });
    totals.push({ label: "DIFERENCIA", value: clp(cuad.diferencia), bold: true });
  }
  if (cuad.ventas_boleta?.cantidad) totals.push({ label: `Boletas (${cuad.ventas_boleta.cantidad})`, value: clp(cuad.ventas_boleta.total) });
  if (cuad.ventas_factura?.cantidad) totals.push({ label: `Facturas (${cuad.ventas_factura.cantidad})`, value: clp(cuad.ventas_factura.total) });
  const tks = cuad.tickets_sin_dte;
  if (tks?.cantidad) {
    totals.push({ label: "Tickets sin DTE", value: String(tks.cantidad) });
    totals.push({ label: "Vendido sin DTE", value: clp(tks.total), bold: true });
  }
  const t = cuad.tbk_cierre;
  if (t) {
    totals.push({ label: "TBK Ai-POS", value: `${t.sistema_cantidad}tx ${clp(t.sistema_total)}` });
    if (t.totales_ok) {
      totals.push({ label: "TBK Equipo", value: `${t.equipo_cantidad}tx ${clp(t.equipo_total)}` });
      totals.push({ label: "TBK Diferencia", value: `${t.dif_cantidad}tx ${clp(t.dif_total)}`, bold: true });
    } else totals.push({ label: "TBK Equipo", value: "sin datos" });
  }
  if (cuad.c2c_sistema?.cantidad) totals.push({ label: "TBK C2C Ai-POS", value: `${cuad.c2c_sistema.cantidad}tx ${clp(cuad.c2c_sistema.total)}` });
  const footer = [`Ventas: ${cuad.num_ventas}`];
  if (t) footer.push(t.cierre_ok ? "CIERRE TRANSBANK REALIZADO" : "CIERRE TRANSBANK NO REALIZADO");
  if (cuad.ventas_nulas) footer.push(`Ventas nulas: ${cuad.ventas_nulas} · ${clp(cuad.monto_nulas)}`);
  if (cuad.ventas_simuladas) footer.push(`Incluye ${clp(cuad.ventas_simuladas)} simulados`);
  return { header, subheader, items, totals, footer, width, feedBeforeCut: ctx.feedBeforeCut };
}

/**
 * Voucher para ingreso/retiro de caja.
 * mov = { tipo: "INGRESO"|"RETIRO", monto, motivo, at?, user_name? }
 */
export function cashMovementToReceipt(mov, ctx = {}) {
  const clp = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
  const width = ctx.width || 32;
  const footer = ["Documento interno - no valido como comprobante tributario"];
  // En RETIRO dejamos espacio al pie para nombre y firma de quien recibe el dinero.
  if (String(mov.tipo || "").toUpperCase().includes("RETIRO")) {
    footer.push("", "", "Nombre: ______________________", "", "Firma:  ______________________", "");
  }
  return {
    header: [ctx.company_name || "Ai-POS", ctx.store_name || ""].filter(Boolean),
    subheader: [
      `VOUCHER DE CAJA - ${mov.tipo}`,
      new Date(mov.at || Date.now()).toLocaleString("es-CL"),
      ctx.caja ? `Caja: ${ctx.caja}` : "",
      ctx.user_name ? `Cajero: ${ctx.user_name}` : "",
    ].filter(Boolean),
    items: [],
    totals: [
      { label: "Tipo", value: mov.tipo },
      { label: "Motivo", value: (mov.motivo || "-").slice(0, width - 12) },
      { label: "MONTO", value: clp(mov.monto), bold: true },
    ],
    footer,
    width,
    feedBeforeCut: ctx.feedBeforeCut,
  };
}

/**
 * Vale de venta suspendida (con código de barras opcional).
 * suspended = { codigo, items:[{cantidad,nombre,precio_unitario}], total, created_at }
 */
/**
 * Vale de venta suspendida (con código de barras opcional).
 * suspended = { codigo, items:[{cantidad,nombre,precio_unitario}], total, created_at }
 */
/** Ticket de cambio (regalo) en 58/80 mm: sin precios, con el N° de venta en código de barras. */
export function exchangeSlipToReceipt(sale, ctx = {}) {
  const num = String(sale?.numero ?? "");
  return {
    header: [ctx.company_name || "Ai-POS", ctx.store_name || ""].filter(Boolean),
    subheader: [
      "TICKET DE CAMBIO",
      `Venta N ${num}`,
      new Date(sale?.created_at || Date.now()).toLocaleString("es-CL"),
      ctx.caja ? `Caja: ${ctx.caja}` : "",
    ].filter(Boolean),
    items: (ctx.items || []).map((it) => ({ qty: it.cantidad, name: it.nombre, price: "" })),
    totals: [],
    barcode: num,
    footer: [
      "Presenta este ticket en",
      "administracion para",
      "gestionar tu cambio",
      "",
      "NO es comprobante de pago",
      "",
    ],
    width: ctx.width || 32,
    feedBeforeCut: ctx.feedBeforeCut,
  };
}

/**
 * Vale de venta suspendida (con código de barras opcional).
 * suspended = { codigo, items:[{cantidad,nombre,precio_unitario}], total, created_at }
 */
export function suspendedToReceipt(suspended, ctx = {}) {  const clp = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
  const width = ctx.width || 32;
  return {
    header: [ctx.company_name || "Ai-POS", ctx.store_name || ""].filter(Boolean),
    subheader: [
      "VENTA SUSPENDIDA",
      new Date(suspended.created_at || Date.now()).toLocaleString("es-CL"),
      ctx.caja ? `Caja: ${ctx.caja}` : "",
      ctx.user_name ? `Cajero: ${ctx.user_name}` : "",
    ].filter(Boolean),
    items: (suspended.items || []).map((it) => ({
      qty: it.cantidad,
      name: it.nombre,
      price: clp(it.cantidad * it.precio_unitario),
    })),
    totals: [
      { label: "TOTAL", value: clp(suspended.total), bold: true },
      { label: "Codigo", value: suspended.codigo },
    ],
    // Código de barras ESC/POS para escanear en caja al recuperar la venta.
    barcode: suspended.codigo,
    footer: [
      "Escanea este codigo en caja",
      "para recuperar la venta",
      "",
      "Este vale NO es comprobante",
      "de pago.",
    ],
    width,
    feedBeforeCut: ctx.feedBeforeCut,
  };
}

/**
 * Vale de concurso — replica el formato del historial de vales impresos:
 * "CONCURSO / <nombre concurso> / <detalle> / QR / <codigo> / Sucursal / Venta Nº / Monto / DEPOSITA TU VALE EN LA TÓMBOLA"
 * contest = { nombre, detalle?, codigo, store_identifier?, sale_number?, sale_total?, fecha? }
 */
export function contestVoucherToReceipt(contest, ctx = {}) {
  const width = ctx.width || 32;
  const clp = (n) => "$" + Math.round(Number(n || 0)).toLocaleString("es-CL");
  const nombre = (contest.nombre || contest.premio_nombre || "CONCURSO").toUpperCase();
  const detalle = contest.detalle || contest.terminos || "";
  return {
    header: ["CONCURSO", nombre],
    subheader: [
      ...(detalle ? [detalle] : []),
      ...(ctx.caja ? [`Caja: ${ctx.caja}`] : []),
      ...(ctx.user_name ? [`Cajero: ${ctx.user_name}`] : []),
    ],
    items: [],
    totals: [
      ...(contest.store_identifier ? [{ label: "Sucursal", value: contest.store_identifier }] : []),
      ...(contest.sale_number ? [{ label: "Venta N", value: String(contest.sale_number) }] : []),
      ...(contest.sale_total ? [{ label: "Monto", value: clp(contest.sale_total) }] : []),
    ],
    // QR real (imagen raster) con el código del vale para escaneo en tómbola/verificación
    qrCode: contest.codigo || null,
    qrScale: 6,
    footer: [
      contest.codigo ? contest.codigo : "",
      "",
    ],
    // Campos a la izquierda: el cliente necesita el ancho completo para escribir
    footerLeft: [
      "Completa tus datos:",
      "",
      "Nombre:",
      "_".repeat(width),
      "",
      "Contacto / Telefono:",
      "_".repeat(width),
      "",
    ],
    footerEnd: [
      "DEPOSITA TU VALE",
      "EN LA TOMBOLA",
      "", "",
    ],
    width,
    feedBeforeCut: ctx.feedBeforeCut,
  };
}
